import type { IconName } from '../../shared/icons.ts';
import { NICKNAME_MAX_LENGTH, type OwnedMonsterView } from '../../shared/protocol.ts';
import { el } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import { typeBadges } from '../common/element-badge.ts';
import { rarityBadge } from '../common/rarity-badge.ts';

export type HpLevel = '' | 'mid' | 'low';

export const hpLevel = (ratio: number): HpLevel => (ratio > 0.5 ? '' : ratio > 0.2 ? 'mid' : 'low');

/** Met à jour une barre `.bar.hp` (couleur selon les PV restants). */
export function setHpBar(bar: HTMLElement, hp: number, maxHp: number): void {
  const ratio = maxHp ? hp / maxHp : 0;
  bar.className = `bar hp ${hpLevel(ratio)}`;
  const fill = bar.firstElementChild as HTMLElement | null;
  if (fill) fill.style.width = `${ratio * 100}%`;
}

export const hpBar = (hp: number, maxHp: number): HTMLDivElement => {
  const bar = el('div', { className: 'bar hp' }, el('div'));
  setHpBar(bar, hp, maxHp);
  return bar;
};

/** Nom du monstre, suivi de ses marques : chromatique, et « me suit ». */
export const monsterName = (monster: OwnedMonsterView): (Node | string)[] => [
  monster.name,
  ...(monster.shiny ? [' ', icon('sparkles', { label: 'Chromatique', className: 'shiny-mark' })] : []),
  ...(monster.following ? [' ', icon('follow', { label: 'Vous suit' })] : []),
];

export interface CardAction {
  label: string;
  /** Icône avant le libellé (seule si le libellé est vide : `title` sert alors de nom au bouton). */
  icon?: IconName;
  iconColor?: string;
  title?: string;
  onClick: () => void;
  variant?: 'secondary' | 'danger';
  /** Seulement sur la carte : dans la fiche, les flèches passent au monstre voisin (Monter / Descendre dans l'équipe). */
  cardOnly?: boolean;
}

/** Passer d'une fiche à la voisine dans une liste (la boîte, l'équipe…). */
export interface MonsterBrowse {
  previous(): void;
  next(): void;
  /** Place dans la liste, par exemple « 3 / 10 ». */
  position: string;
}

/**
 * Ouvre la fiche d'un monstre ; `actions` : celles de sa carte, reprises dans la fiche (seul accès sur mobile,
 * où la carte est réduite) ; `browse` : flèches vers les monstres voisins de la liste d'où l'on vient.
 */
export type OpenMonster = (monster: OwnedMonsterView, actions: CardAction[], browse?: MonsterBrowse) => void;

export function monsterCard(monster: OwnedMonsterView, actions: CardAction[] = [], onRename?: (nickname: string) => void, onOpen?: OpenMonster): HTMLElement {
  const stats = `${monster.hp}/${monster.maxHp} PV · ATQ ${monster.atk} · DEF ${monster.def} · VIT ${monster.spd}`;
  const name = el('strong', {}, ...monsterName(monster));
  if (monster.nickname) name.title = monster.speciesName;
  const title = el('div', { className: 'title' }, name, el('span', { textContent: `Nv ${monster.level}` }));
  if (onRename) name.after(renameButton(monster, name, onRename));
  const card = el('div', { className: `mon-card ${monster.shiny ? 'is-shiny' : ''}` },
    el('img', { src: monster.image, alt: monster.name, className: monster.shiny ? 'shiny' : '' }),
    el('div', { className: 'body' },
      title,
      el('div', { className: 'badges' }, ...typeBadges(monster), rarityBadge(monster.rarity)),
      hpBar(monster.hp, monster.maxHp),
      el('small', { textContent: stats }),
    ),
  );
  if (onOpen) {
    // Un clic sur la carte (hors boutons et champ de surnom) ouvre la fiche détaillée.
    card.classList.add('openable');
    card.title = 'Voir la fiche';
    card.addEventListener('click', (event) => {
      if (!(event.target as Element).closest('button, input')) onOpen(monster, actions);
    });
  }
  if (actions.length) card.append(actionBar(actions));
  return card;
}

/** Rangée de boutons d'actions (carte d'un monstre, ou sa fiche). */
export function actionBar(actions: CardAction[], className = 'card-actions'): HTMLElement {
  return el('div', { className }, ...actions.map(({ label, icon: iconName, iconColor, title, onClick, variant }) => {
    const button = el('button', { className: variant ?? '', title: title ?? '' },
      ...(iconName ? [icon(iconName, { color: iconColor })] : []), ...(label ? [iconName ? ` ${label}` : label] : []));
    if (!label && title) button.setAttribute('aria-label', title);
    button.addEventListener('click', onClick);
    return button;
  }));
}

/** Bouton crayon qui remplace le nom par un champ de saisie (Entrée pour valider, Échap pour annuler). */
function renameButton(monster: OwnedMonsterView, name: HTMLElement, onRename: (nickname: string) => void): HTMLButtonElement {
  const button = el('button', { className: 'icon rename', title: 'Donner un surnom', ariaLabel: 'Donner un surnom' }, icon('rename'));
  button.addEventListener('click', () => {
    const input = el('input', { value: monster.nickname ?? '', placeholder: monster.speciesName, maxLength: NICKNAME_MAX_LENGTH, className: 'rename-input' });
    const finish = (save: boolean) => {
      if (save) onRename(input.value);
      input.replaceWith(name);
      button.hidden = false;
    };
    input.addEventListener('keydown', (event) => {
      event.stopPropagation(); // ne pas déplacer le joueur en tapant
      if (event.key === 'Enter') finish(true);
      if (event.key === 'Escape') finish(false);
    });
    input.addEventListener('blur', () => { if (input.isConnected) finish(true); });
    name.replaceWith(input);
    button.hidden = true;
    input.focus();
    input.select();
  });
  return button;
}
